import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IoClose } from "react-icons/io5";
import notesService from "../Services/notesService";
import SecondaryButton from "../Components/SecondaryButton";
import { MdOutlineAdd } from "react-icons/md";

const NoteModal = ({ isOpen, onClose, onSave, note, setNote }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    title: "",
    description: "",
    priority: "",
  });

  const [errors, setErrors] = useState({
    title: false,
    description: false,
    priority: false,
  });

  const [regex] = useState({
    title: /.{1,100}$/,
    description: /.{1,1000}$/,
    priority: /^(low|medium|high|urgent)$/,
  });

  const [loading, setLoading] = useState(false);
  const [responseError, setResponseError] = useState("");

  const handleChange = (value, field) => {
    setResponseError("");
    setData((prev) => ({ ...prev, [field]: value }));
    if (value.length > 0) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = async () => {
    let hasError = false;
    Object.keys(data).forEach((field) => {
      if (!regex[field]?.test(data[field])) {
        setErrors((prev) => ({ ...prev, [field]: true }));
        hasError = true;
      }
    });

    if (hasError) {
      toast.error("Please fill all fields correctly");
      return;
    }

    try {
      setLoading(true);

      if (note?.id) {
        await notesService.udpate(note.id, data);
        toast.success("Note updated successfully!");
      } else {
        await notesService.create(data);
        toast.success("Note added successfully!");
      }

      onSave(); // Refetch data
      onClose(); // Close modal
      setNote({ title: "", description: "", priority: "" }); // Reset selected note
      setData({ title: "", description: "", priority: "" }); // Reset form
    } catch (error) {
      toast.error("Failed to save note");
      setResponseError(error?.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (note?.id) {
      setData({
        title: note.title || "",
        description: note.description || "",
        priority: note.priority || "",
      });
    } else {
      setData({ title: "", description: "", priority: "" });
    }
  }, [note, isOpen]);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl border border-yellow-100 space-y-5 font-serif">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-2xl font-bold text-secondary tracking-wide">
              Notepad
            </Dialog.Title>
            <button onClick={onClose}>
              <IoClose className="h-6 w-6 text-gray-500 hover:text-red-500" />
            </button>
          </div>

          <div className="space-y-4">
            <input
              type="text"
              placeholder="Title"
              value={data.title}
              onChange={(e) => handleChange(e.target.value, "title")}
              className={`primary-input ${
                errors.title ? "border-red-500" : ""
              }`}
            />
            <textarea
              rows="10"
              placeholder="Start typing your note..."
              value={data.description}
              onChange={(e) => handleChange(e.target.value, "description")}
              className={`primary-input max-h-[300px] ${
                errors.description ? "border-red-500" : "border-gray-100"
              }`}
              // style={{
              //   backgroundImage:
              //     "repeating-linear-gradient(white, white 25px, #fef9c3 26px)",
              //   backgroundSize: "100% 26px",
              // }}
            />
            <select
              value={data.priority}
              onChange={(e) => handleChange(e.target.value, "priority")}
              className={`primary-input ${
                errors.priority ? "border-red-500" : ""
              }`}
            >
              <option value="">Select Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {responseError && (
            <p className="text-red-500 text-sm pt-1">{responseError}</p>
          )}

          <div className="flex justify-center pt-4 gap-3">
            <SecondaryButton
              text={loading ? "Saving..." : "Save Note"}
              Icon={MdOutlineAdd}
              onClick={handleSubmit}
            />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default NoteModal;
